import React, { createContext, useContext, useState, useEffect } from 'react';
import { setSentryUser, addBreadcrumb, captureException } from '../services/sentry';
import { login as apiLogin } from '../services/ApiService';
import { LoginRequest } from '../models/SafetyPortalTypes';

interface AuthContextType {
  isLoggedIn: boolean;
  setIsLoggedIn: (value: boolean) => void;
  login: (username: string, password: string, newPassword?: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('accessToken');
    if (token) {
      setIsLoggedIn(true);
      // Set user context in Sentry if we have user info
      const userName = localStorage.getItem('userName');
      if (userName) {
        setSentryUser(userName, userName, undefined);
      }
    }
  }, []);

  const login = async (username: string, password: string, newPassword?: string) => {
    try {
      addBreadcrumb('Attempting login', 'auth', 'info', { username });
      const loginData: LoginRequest = {
        userName: username,
        password,
        action: 'LOGIN',
        ...(newPassword && { newPassword }),
      };
      const response = await apiLogin(loginData);
      
      if (response.tokens) {
        setIsLoggedIn(true);
        localStorage.setItem('accessToken', response.tokens.accessToken);
        localStorage.setItem('refreshToken', response.tokens.refreshToken);
        localStorage.setItem('idToken', response.tokens.idToken);
        localStorage.setItem('userName', username);

        // Set user context in Sentry
        setSentryUser(username, username, undefined);
        addBreadcrumb('Login successful', 'auth', 'info', { username });
      } else {
        throw new Error('No tokens received from login');
      }
    } catch (error) {
      captureException(error instanceof Error ? error : new Error(String(error)), {
        tags: {
          source: 'AuthContext',
          action: 'login',
        },
      });
      throw error;
    }
  };

  const logout = () => {
    setIsLoggedIn(false);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('userName');
    setSentryUser(null);
    addBreadcrumb('User logged out', 'auth', 'info');
  };

  return (
    <AuthContext.Provider value={{ isLoggedIn, setIsLoggedIn, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

